import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Trash2, HardDrive, Video, Music, Baby, ListVideo } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { api } from "../lib/api";
import { toast } from "sonner";

const formatBytes = (bytes) => {
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  if (mb > 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(0)} MB`;
};

export const Header = ({ onNewProject }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [usageBytes, setUsageBytes] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadUsage = async () => {
    try {
      const res = await api.get("/media/usage");
      setUsageBytes(res.data.bytes);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadUsage();
  }, []);

  const cleanMedia = async () => {
    setCleaning(true);
    try {
      const res = await api.post("/media/cleanup");
      toast.success(`Freed ${formatBytes(res.data.freed_bytes)} from ${res.data.projects_cleared} project(s)`);
      loadUsage();
    } catch (e) {
      toast.error("Failed to clean up media");
    } finally {
      setCleaning(false);
      setConfirmOpen(false);
    }
  };

  return (
    <header
      data-testid="nav-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/80"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-black group-hover:rotate-6 transition-transform">
            <Sparkles className="w-4 h-4" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight text-slate-50">
            AutoTube <span className="text-amber-500">AI</span>
          </span>
        </a>

        <nav className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-xl p-1">
          <button
            data-testid="nav-video-maker"
            onClick={() => navigate("/")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              location.pathname === "/" ? "bg-amber-500 text-black" : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            <Video className="w-4 h-4" /> <span className="hidden sm:inline">Video Maker</span>
          </button>
          <button
            data-testid="nav-song-maker"
            onClick={() => navigate("/songs")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              location.pathname === "/songs" ? "bg-fuchsia-500 text-black" : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            <Music className="w-4 h-4" /> <span className="hidden sm:inline">Song / Bhajan</span>
          </button>
          <button
            data-testid="nav-rhymes-maker"
            onClick={() => navigate("/rhymes")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              location.pathname === "/rhymes" ? "bg-teal-400 text-black" : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            <Baby className="w-4 h-4" /> <span className="hidden sm:inline">Kids Rhymes</span>
          </button>
          <button
            data-testid="nav-compiler"
            onClick={() => navigate("/compiler")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              location.pathname === "/compiler" ? "bg-indigo-400 text-black" : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            <ListVideo className="w-4 h-4" /> <span className="hidden sm:inline">Compiler</span>
          </button>
        </nav>

        <div className="flex items-center gap-3">
          {usageBytes !== null && (
            <span
              data-testid="media-usage-badge"
              className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-slate-500 border border-slate-800 px-3 py-1.5 rounded-full"
            >
              <HardDrive className="w-3.5 h-3.5" /> {formatBytes(usageBytes)}
            </span>
          )}

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <button
              data-testid="clean-media-button"
              onClick={() => setConfirmOpen(true)}
              className="bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700/60 px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all text-sm"
            >
              <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">Clean Media</span>
            </button>
            <AlertDialogContent className="bg-slate-900 border-slate-800 text-slate-100">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Clean up generated media?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-500">
                  This permanently deletes the local video/thumbnail files for completed, failed, and
                  stuck (interrupted repeatedly, no progress for 20+ minutes) projects to free up disk
                  space. Already-published videos remain safe on YouTube, but they won&apos;t be
                  replayable or re-downloadable here afterwards.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="clean-media-cancel-button" className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="clean-media-confirm-button"
                  onClick={cleanMedia}
                  disabled={cleaning}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  {cleaning ? "Cleaning..." : "Clean Up"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            data-testid="new-project-button"
            onClick={onNewProject}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-4 py-2 rounded-xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
          >
            New Project
          </Button>
        </div>
      </div>
    </header>
  );
};
